import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import {
  ClipboardList, RefreshCw, Download, Trash2,
  ChevronLeft, ChevronRight,
} from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import LoadingSpinner from '../../components/LoadingSpinner';
import EmptyState from '../../components/EmptyState';
import * as assessmentsApi from '../../api/assessments.api';
import { errorMessage } from '../../api/client';
import { useAuth } from '../../hooks/useAuth';
import { ROLES } from '../../utils/roles';

// Admin / super-admin view of every completed landing-page /intake
// assessment — including terminal-block runs where the visitor never signed
// up. Rows are keyed by the client-side assessment number so the care team
// can match WhatsApp references back to the underlying inputs.

const CATEGORY_BADGE = {
  ELIGIBLE:        'badge success',
  CONDITIONAL:     'badge warning',
  NOT_ELIGIBLE:    'badge danger',
  UNDER_18:        'badge danger',
  LOW_BMI:         'badge warning',
  URGENT_SYMPTOMS: 'badge danger',
  OTHER:           'badge',
};

function Assessments() {
  const { user } = useAuth();
  const canDelete = user?.role === ROLES.SUPER_ADMIN;

  const [data, setData] = useState({ items: [], total: 0, totalPages: 1, categories: [] });
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    resultCategory: '', search: '', from: '', to: '',
    page: 1, pageSize: 50,
  });
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => { if (v !== '' && v != null) params[k] = v; });
      const fresh = await assessmentsApi.listAssessments(params);
      setData(fresh || { items: [] });
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  function setFilter(k, v) {
    setFilters((s) => ({ ...s, [k]: v, page: 1 }));
  }
  function gotoPage(delta) {
    setFilters((s) => ({ ...s, page: Math.max(1, Math.min(data.totalPages, s.page + delta)) }));
  }
  function clearFilters() {
    setFilters({ resultCategory: '', search: '', from: '', to: '', page: 1, pageSize: 50 });
  }

  async function onExport() {
    try {
      const params = {};
      ['resultCategory', 'search', 'from', 'to'].forEach((k) => {
        if (filters[k]) params[k] = filters[k];
      });
      const blob = await assessmentsApi.exportAssessmentsCsv(params);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `assessments-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(errorMessage(e));
    }
  }

  async function onDelete(id, num) {
    if (!window.confirm(`Delete assessment ${num}? This cannot be undone.`)) return;
    try {
      await assessmentsApi.deleteAssessment(id);
      toast.success('Deleted');
      load();
    } catch (e) {
      toast.error(errorMessage(e));
    }
  }

  return (
    <>
      <PageHeader
        title="Assessments"
        subtitle="Every completed landing-page /intake assessment, keyed by assessment number."
        actions={
          <div className="row" style={{ gap: 8 }}>
            <button className="btn btn-outline" onClick={onExport} disabled={loading || !data.total}>
              <Download size={14} /> Export CSV
            </button>
            <button className="btn btn-outline" onClick={load} disabled={loading}>
              <RefreshCw size={14} /> Refresh
            </button>
          </div>
        }
      />

      <div className="card" style={{ marginBottom: 'var(--s-3)' }}>
        <div className="grid grid-4" style={{ gap: 'var(--s-3)' }}>
          <div className="field" style={{ marginBottom: 0 }}>
            <label>Result</label>
            <select
              className="input"
              value={filters.resultCategory}
              onChange={(e) => setFilter('resultCategory', e.target.value)}
            >
              <option value="">All results</option>
              {(data.categories || []).map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
          <div className="field" style={{ marginBottom: 0 }}>
            <label>Search</label>
            <input
              type="text"
              className="input"
              placeholder="MYD-…, email, phone, name"
              value={filters.search}
              onChange={(e) => setFilter('search', e.target.value)}
            />
          </div>
          <div className="field" style={{ marginBottom: 0 }}>
            <label>From</label>
            <input
              type="date"
              className="input"
              value={filters.from}
              onChange={(e) => setFilter('from', e.target.value)}
            />
          </div>
          <div className="field" style={{ marginBottom: 0 }}>
            <label>To</label>
            <input
              type="date"
              className="input"
              value={filters.to}
              onChange={(e) => setFilter('to', e.target.value)}
            />
          </div>
        </div>
        <div className="row" style={{ gap: 8, marginTop: 'var(--s-3)' }}>
          <button className="btn btn-link" onClick={clearFilters}>Clear filters</button>
          <span className="muted text-sm">
            {data.total ? `${data.total.toLocaleString()} assessments` : '—'}
          </span>
        </div>
      </div>

      {loading ? (
        <LoadingSpinner />
      ) : !data.items.length ? (
        <EmptyState
          icon={ClipboardList}
          title="No assessments yet"
          description="Assessments completed on /intake will appear here."
        />
      ) : (
        <div className="card" style={{ overflowX: 'auto' }}>
          <table className="table">
            <thead>
              <tr>
                <th>Assessment #</th>
                <th>When</th>
                <th>Contact</th>
                <th>BMI</th>
                <th>Result</th>
                <th>Recommended</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((r) => (
                <React.Fragment key={r._id}>
                  <tr>
                    <td className="cell-mono">{r.assessmentNumber}</td>
                    <td className="cell-mono text-xs">{new Date(r.createdAt).toLocaleString()}</td>
                    <td>
                      <div>{r.basicInfo?.name || <span className="muted">—</span>}</div>
                      <div className="muted text-xs">
                        {r.basicInfo?.contactEmail || r.basicInfo?.contactPhone || ''}
                      </div>
                    </td>
                    <td>{r.basicInfo?.bmi ?? '—'}</td>
                    <td>
                      <span className={CATEGORY_BADGE[r.resultCategory] || 'badge'}>
                        {r.resultCategory || '—'}
                      </span>
                    </td>
                    <td>
                      {r.recommendedBottles
                        ? `${r.recommendedBottles} bottle${r.recommendedBottles > 1 ? 's' : ''} · ${r.recommendedWeeks}w`
                        : '—'}
                    </td>
                    <td>
                      <button
                        className="btn btn-link"
                        onClick={() => setExpanded(expanded === r._id ? null : r._id)}
                      >
                        {expanded === r._id ? 'Hide' : 'Details'}
                      </button>
                      {canDelete && (
                        <button
                          className="btn btn-link"
                          onClick={() => onDelete(r._id, r.assessmentNumber)}
                          title="Delete assessment"
                          style={{ marginLeft: 4 }}
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </td>
                  </tr>
                  {expanded === r._id && (
                    <tr>
                      <td colSpan={7} style={{ background: 'var(--ink-100)' }}>
                        <div className="cell-mono text-xs" style={{ whiteSpace: 'pre-wrap' }}>
                          <strong>Reason:</strong> {r.resultReason || '—'}{'\n'}
                          <strong>Linked user:</strong> {r.user
                            ? `${r.user.name || ''} <${r.user.email || ''}>`
                            : '(not signed up)'}{'\n'}
                          <strong>IP:</strong> {r.ipAddress || '—'}{'\n'}
                          <strong>Basic info:</strong>{'\n'}
                          {JSON.stringify(r.basicInfo || {}, null, 2)}{'\n\n'}
                          <strong>Payload:</strong>{'\n'}
                          {JSON.stringify(r.payload || {}, null, 2)}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>

          <div className="row between" style={{ marginTop: 'var(--s-3)', alignItems: 'center' }}>
            <div className="muted text-sm">
              Page {data.page || filters.page} of {data.totalPages || 1}
            </div>
            <div className="row" style={{ gap: 6 }}>
              <button
                className="btn btn-outline"
                onClick={() => gotoPage(-1)}
                disabled={(data.page || 1) <= 1}
              >
                <ChevronLeft size={14} /> Prev
              </button>
              <button
                className="btn btn-outline"
                onClick={() => gotoPage(+1)}
                disabled={(data.page || 1) >= (data.totalPages || 1)}
              >
                Next <ChevronRight size={14} />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default Assessments;
